import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

export type AnalyticsBylineGateReason =
	| 'free-edition'
	| 'logged-out'
	| 'anonymous-access'
	| 'external-share-access'
	| 'customer-account';

type AnalyticsBylineEligibilityInput = {
	edition: string | null;
	isLoggedIn: boolean;
	accessStatus: AccessStatus | null;
	userId: string | null;
};

/**
 * Returns only cohorts that are already known not to consume the paid internal-user byline.
 * External collaborators, unlicensed users, and unknown/null statuses intentionally fail open
 * until the shadow comparison proves that SessionData is equivalent to permissionType.
 */
export const getAnalyticsBylineGateReason = ({
	edition,
	isLoggedIn,
	accessStatus,
	userId,
}: AnalyticsBylineEligibilityInput): AnalyticsBylineGateReason | null => {
	if (edition === 'FREE') {
		return 'free-edition';
	}
	if (!isLoggedIn) {
		return 'logged-out';
	}
	if (userId?.startsWith('qm:')) {
		return 'customer-account';
	}
	if (accessStatus === AccessStatus.ANONYMOUS_ACCESS) {
		return 'anonymous-access';
	}
	if (accessStatus === AccessStatus.EXTERNAL_SHARE_ACCESS) {
		return 'external-share-access';
	}
	return null;
};

/** Candidate used only for shadow validation. null means that no safe comparison is possible. */
export const getSessionDataInternalUserCandidate = (
	accessStatus: AccessStatus | null,
): boolean | null => {
	switch (accessStatus) {
		case AccessStatus.LICENSED_ADMIN_ACCESS:
		case AccessStatus.LICENSED_USE_ACCESS:
			return true;
		case AccessStatus.ANONYMOUS_ACCESS:
		case AccessStatus.EXTERNAL_COLLABORATOR_ACCESS:
		case AccessStatus.EXTERNAL_SHARE_ACCESS:
		case AccessStatus.NOT_PERMITTED:
		case AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS:
			return false;
		default:
			return null;
	}
};

export const getAnalyticsBylineAccessClass = (
	accessStatus: AccessStatus | null,
):
	| 'licensed'
	| 'externalCollaborator'
	| 'unlicensedAuthenticated'
	| 'anonymousOrExternalShare'
	| 'notPermitted'
	| 'missing' => {
	switch (accessStatus) {
		case AccessStatus.LICENSED_ADMIN_ACCESS:
		case AccessStatus.LICENSED_USE_ACCESS:
			return 'licensed';
		case AccessStatus.EXTERNAL_COLLABORATOR_ACCESS:
			return 'externalCollaborator';
		case AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS:
			return 'unlicensedAuthenticated';
		case AccessStatus.ANONYMOUS_ACCESS:
		case AccessStatus.EXTERNAL_SHARE_ACCESS:
			return 'anonymousOrExternalShare';
		case AccessStatus.NOT_PERMITTED:
			return 'notPermitted';
		default:
			return 'missing';
	}
};
